import React, { useState, useEffect } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import "./FilterRow.css";

/**
 * FilterRow Component
 * Represents a single filter row with field selection, operator, and values
 * Optimized for drawer layout
 * 
 * Reusable across all modules - place in Shared/newfilterui
 */
const FilterRow = ({
    module,
    filter,
    availableFields,
    onUpdate,
    onRemove,
    canRemove = true,
    onFetchValues,
    hasOtherFilters = false,
}) => {
    const [operatorOptions, setOperatorOptions] = useState([]);
    const [valueOptions, setValueOptions] = useState([]);
    const [isLoadingValues, setIsLoadingValues] = useState(false);

    const fieldOptions = availableFields.map((field) => ({
        value: field.key,
        label: field.label,
    }));

    const formatValueLabel = (value) => {
        if (typeof value !== "string") return value;
        return value
            .split("_")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Map operator types to readable labels
    const operatorLabels = {
        is: "Is",
        contains: "Contains",
        equals: "Equals",
        gt: "Greater Than",
        lt: "Less Than",
        between: "Between",
        gte: "Greater Than or Equal",
        lte: "Less Than or Equal",
        in: "In",
        exist: "Exist",
        "not exist": "Not Exist",
    };

    // Helper to get allowed operators based on field type
    const getOperatorsByType = (type, field = null) => {
        if (field && field.operators && field.operators.length > 0) {
            return field.operators.map(op => ({
                value: op,
                label: operatorLabels[op] || op.charAt(0).toUpperCase() + op.slice(1)
            }));
        }

        const operatorMap = {
            text: [
                { value: "contains", label: "Contains" },
                { value: "is", label: "Is" },
            ],
            string: [
                { value: "contains", label: "Contains" },
                { value: "is", label: "Is" },
            ],
            number: [
                { value: "equals", label: "Equals" },
                { value: "gt", label: "Greater Than" },
                { value: "lt", label: "Less Than" },
                { value: "between", label: "Between" },
                { value: "gte", label: "Greater Than or Equal" },
                { value: "lte", label: "Less Than or Equal" },
            ],
            enum: [
                { value: "is", label: "Is" },
                { value: "in", label: "In" },
            ],
            date: [
                { value: "between", label: "Between" },
                { value: "equals", label: "Equals" },
                { value: "is", label: "Is" },
            ],
            boolean: [
                { value: "exist", label: "Exist" },
                { value: "not exist", label: "Not Exist" },
            ],
        };
        return operatorMap[type] || operatorMap.text;
    };

    /**
     * Handle field selection change
     */
    const handleFieldChange = (selectedField) => {
        const field = availableFields.find((f) => f.key === selectedField);
        if (field) {
            const currentOperators = getOperatorsByType(field.type, field);
            setOperatorOptions(currentOperators);

            onUpdate({
                key: field.key,
                label: field.label,
                type: field.type,
                op: currentOperators[0]?.value || "",
                values: [],
                allowedOperators: currentOperators.map((op) => op.value),
            });

            fetchUniqueValues(field.key);
        } else {
            onUpdate({
                key: "",
                label: "",
                type: "",
                op: "",
                values: [],
                allowedOperators: [],
            });
        }
    };

    /**
     * Handle operator change
     */
    const handleOperatorChange = (operator) => {
        if (operator === "is" || operator === "equals" || operator === "between") {
            onUpdate({ op: operator, values: [] });
        } else {
            onUpdate({ op: operator });
        }
    };

    /**
     * Handle value selection/input changes
     */
    const handleValuesChange = (values) => {
        onUpdate({ values });
    };

    /**
     * Parse backend dates reliably to prevent breaking react-datepicker
     */
    const getValidIncludeDates = () => {
        if (valueOptions.length === 0) return undefined;

        const validDates = [];
        valueOptions.forEach(opt => {
            const m = moment(opt.value, ["YYYY-MM-DD", "DD-MM-YYYY", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
            if (m.isValid()) {
                validDates.push(m.toDate());
            }
        });

        return validDates.length > 0 ? validDates : undefined;
    };

    /**
     * Fetch unique values for a specific field via search/filter logic
     */
    const fetchUniqueValues = async (fieldKey) => {
        if (!fieldKey) return;

        const field = availableFields.find(f => f.key === fieldKey);
        if (field && field.values && field.values.length > 0) {
            setValueOptions(field.values.map(v => ({ value: v, label: formatValueLabel(v) })));
            return;
        }

        setIsLoadingValues(true);
        try {
            if (onFetchValues) {
                const values = await onFetchValues(fieldKey);
                if (values) {
                    setValueOptions(values.map((v) => ({ value: v, label: formatValueLabel(v) })));
                } else {
                    setValueOptions([]);
                }
            } else {
                setValueOptions([]);
            }
        } catch (error) {
            console.error("Error fetching unique values:", error);
            setValueOptions([]);
        } finally {
            setIsLoadingValues(false);
        }
    };

    // Sync operator options when filter type or key is set
    useEffect(() => {
        if (filter.type) {
            const field = availableFields.find((f) => f.key === filter.key);
            setOperatorOptions(getOperatorsByType(filter.type, field));
        }
    }, [filter.type, filter.key, availableFields]);

    // Fetch initial unique values if key is present
    useEffect(() => {
        const needsValues = filter.key &&
            filter.type !== 'number' &&
            filter.op !== 'contains' &&
            filter.op !== '';

        if (needsValues && valueOptions.length === 0 && !isLoadingValues) {
            fetchUniqueValues(filter.key);
        }
    }, [filter.key, filter.type, filter.op]);

    const hideValueField = filter.op === "exist" || filter.op === "not exist";
    const isIndentModule = module && (module.includes('indent_tracking') || module.includes('indent_request') || module.includes('indent') || module === 'spot');

    const startDate = filter.values[0] ? moment(filter.values[0]).toDate() : null;
    const endDate = filter.values[1] ? moment(filter.values[1]).toDate() : null;
    
    let minDateForStart = (isIndentModule && endDate) ? moment(endDate).subtract(3, 'months').toDate() : null;
    let maxDateForStart = endDate || null;
    let minDateForEnd = startDate || null;
    let maxDateForEnd = (isIndentModule && startDate) ? moment(startDate).add(3, 'months').toDate() : null;

    const isSpotModule = module === 'spot';
    const isRestrictedSpotField = isSpotModule && Boolean(
        filter.key && (
            filter.key.toLowerCase().includes('created') || 
            filter.key.toLowerCase().includes('placement') ||
            (filter.label && filter.label.toLowerCase().includes('created')) ||
            (filter.label && filter.label.toLowerCase().includes('placement'))
        )
    );

    let minDateForSingle = null;

    if (isRestrictedSpotField) {
        const threeMonthsAgo = moment().subtract(3, 'months').toDate();

        if (!minDateForStart || moment(minDateForStart).isBefore(threeMonthsAgo)) {
            minDateForStart = threeMonthsAgo;
        }

        if (!minDateForEnd || moment(minDateForEnd).isBefore(threeMonthsAgo)) {
            minDateForEnd = startDate ? moment.max(moment(startDate), moment(threeMonthsAgo)).toDate() : threeMonthsAgo;
        }

        minDateForSingle = threeMonthsAgo;
    }
    return (
        <div className={`filter-row ${hideValueField ? 'no-value' : ''}`}>
            <div className="filter-row-content">
                <div className="filter-config-row">
                    <div className="filter-field filter-field-config">
                        <label className="filter-label">Field</label>
                        <select
                            className="filter-select field-select"
                            value={filter.key}
                            onChange={(e) => handleFieldChange(e.target.value)}
                        >
                            <option value="">Select field...</option>
                            {fieldOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-field filter-operator-config">
                        <label className="filter-label">Operator</label>
                        <select
                            className="filter-select operator-select"
                            value={filter.op}
                            onChange={(e) => handleOperatorChange(e.target.value)}
                            disabled={!filter.key}
                        >
                            <option value="" disabled>Select...</option>
                            {operatorOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {!hideValueField && (
                    <div className="filter-field filter-value-row">
                        <label className="filter-label">Value</label>
                    {filter.type === "number" &&
                        (filter.op === "between" ||
                            filter.op === "gt" ||
                            filter.op === "lt" ||
                            filter.op === "equals" ||
                            filter.op === "gte" ||
                            filter.op === "lte") ? (
                        <div className="number-input-container">
                            {filter.op === "between" ? (
                                <>
                                    <input
                                        type="number"
                                        className="filter-input number-input"
                                        placeholder="Min"
                                        value={filter.values[0] || ""}
                                        onChange={(e) => {
                                            const newValues = [...filter.values];
                                            newValues[0] = e.target.value;
                                            handleValuesChange(newValues);
                                        }}
                                    />
                                    <span className="between-separator">to</span>
                                    <input
                                        type="number"
                                        className="filter-input number-input"
                                        placeholder="Max"
                                        value={filter.values[1] || ""}
                                        onChange={(e) => {
                                            const newValues = [...filter.values];
                                            newValues[1] = e.target.value;
                                            handleValuesChange(newValues);
                                        }}
                                    />
                                </>
                            ) : (
                                <input
                                    type="number"
                                    className="filter-input number-input"
                                    placeholder="Enter value"
                                    value={filter.values[0] || ""}
                                    onChange={(e) => handleValuesChange([e.target.value])}
                                />
                            )}
                        </div>
                    ) : filter.type === "date" ? (
                        <div className="date-input-container">
                            {filter.op === "between" ? (
                                <>
                                    <DatePicker
                                        selected={filter.values[0] ? moment(filter.values[0]).toDate() : null}
                                        onChange={(date) => {
                                            const newValues = [...filter.values];
                                            newValues[0] = date ? moment(date).format("YYYY-MM-DD") : "";
                                            handleValuesChange(newValues);
                                        }}
                                        dateFormat="yyyy-MM-dd"
                                        className="filter-input date-input"
                                        placeholderText="Start Date"
                                        minDate={minDateForStart}
                                        maxDate={maxDateForStart}
                                        isClearable
                                        showYearDropdown
                                        scrollableYearDropdown
                                        portalId="root"
                                    />
                                    <span className="between-separator">to</span>
                                    <DatePicker
                                        selected={filter.values[1] ? moment(filter.values[1]).toDate() : null}
                                        onChange={(date) => {
                                            const newValues = [...filter.values];
                                            newValues[1] = date ? moment(date).format("YYYY-MM-DD") : "";
                                            handleValuesChange(newValues);
                                        }}
                                        dateFormat="yyyy-MM-dd"
                                        className="filter-input date-input"
                                        placeholderText="End Date"
                                        minDate={minDateForEnd}
                                        maxDate={maxDateForEnd}
                                        isClearable
                                        showYearDropdown
                                        scrollableYearDropdown
                                        portalId="root"
                                    />
                                </>
                            ) : (
                                <DatePicker
                                    selected={filter.values[0] ? moment(filter.values[0]).toDate() : null}
                                    onChange={(date) => {
                                        handleValuesChange([date ? moment(date).format("YYYY-MM-DD") : ""]);
                                    }}
                                    dateFormat="yyyy-MM-dd"
                                    className="filter-input date-input"
                                    placeholderText="Select Date"
                                    includeDates={getValidIncludeDates()}
                                    minDate={minDateForSingle}
                                    isClearable
                                    showYearDropdown
                                    scrollableYearDropdown
                                    portalId="root"
                                />
                            )}
                        </div>
                    ) : filter.op === "contains" ? (
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="Type to search..."
                            value={filter.values[0] || ""}
                            onChange={(e) => handleValuesChange([e.target.value])}
                        />
                    ) : (
                        <MultiSelectDropdown
                            options={valueOptions}
                            selectedValues={filter.values}
                            onChange={handleValuesChange}
                            onOpen={() => fetchUniqueValues(filter.key)}
                            placeholder={
                                isLoadingValues ? "Loading options..." : "Select values..."
                            }
                            disabled={!filter.key || !filter.op || isLoadingValues}
                            singleSelect={filter.key === "bucket" || filter.label?.toLowerCase() === "bucket" || (module === "spot" && filter.key === "quotation_received")}
                        />
                    )}
                    </div>
                )}

                {/* Remove Button - Positioned absolutely via CSS */}
                {canRemove && (
                    <button
                        className="remove-filter-btn"
                        onClick={onRemove}
                        title="Remove filter"
                    >
                        <i className="fa fa-times" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default FilterRow;
